import { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { PageBackButton } from "@/shared/components/PageBackButton";
import { PageSpinner } from "@/shared/components/spinner";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { adaptMedication } from "@/features/patient/medications/utils/adaptMedication";
import { ISSUE_LABELS } from "@/features/patient/medications/components/medicationReviewBadges";
import { ReviewRecords, isPairItem } from "@/features/patient/medications/components/ReviewConflictCard";
import {
  getPhysicianReviewItemApi,
  releaseReviewItemApi,
  type PhysicianReviewItem,
} from "@/portals/physician/api/reviewQueueApi";
import { AttachedViaTag, ReviewStatusBadge, RxNormBlock } from "./reviewBadges";
import { DecisionPanel } from "./DecisionPanel";
import { usePhysicianReviewLive } from "../hooks/usePhysicianReviewLive";
import { physicianQueryKeys } from "@/portals/physician/api/physicianQueryKeys";
import { useCachedQuery } from "@/shared/hooks/useCachedQuery";
import { ReviewHistory } from "./ReviewHistory";

export const MESSAGES = {
  released: "Item released to the shared queue.",
  notReleasable: "This item can't be released right now.",
  notFound: "This item isn't available.",
  loadFailed: "Couldn't load this item. Please try again.",
  noAnswer: "No answer yet",
};

function formatDate(value: string) {
  // yyyy-mm-dd is a calendar date — build it locally so it never shifts a day by timezone.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = dateOnly ? new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]) : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const nonEmpty = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

// Records carry the full MedicationRequest field set (same keys as /api/patient/records),
// so they go through the patient adapter unchanged: dose_quantity/dose_unit,
// frequency, dosage_text, requester_ref, authored_on. The one physician-only
// rule: a missing or empty authored_on falls back to the record's createdAt,
// never to the adapter's 1970 placeholder (patient screens unchanged).
function toMedications(item: PhysicianReviewItem) {
  return item.records.map((r) =>
    adaptMedication({
      id: r.id, resourceType: "MedicationRequest", sourceVendor: r.sourceVendor, uploadId: null,
      confidenceScore: 1, needsReview: false, reviewReason: null, createdAt: r.createdAt, updatedAt: r.createdAt,
      fields: { ...r.fields, authored_on: nonEmpty(r.fields.authored_on) || r.createdAt },
      review: { status: "UNDER_REVIEW", reviewItemId: item.id, recordRole: r.recordRole, triggerType: item.triggerType },
      origin: null,
    }),
  );
}

type ScreenState =
  | { kind: "loading" }
  | { kind: "ready"; item: PhysicianReviewItem }
  | { kind: "not-found" }
  | { kind: "load-failed" };

/** The shared "←" back control (sits in the header row instead of taking a row of its own). */
function BackArrow({ onClick, label }: { onClick: () => void; label: string }) {
  return <PageBackButton onClick={onClick} ariaLabel={`Back to ${label}`} />;
}

interface PhysicianReviewItemViewProps {
  itemId: string;
  /** Where Back goes, for its label ("My Items" / "Review Queue"). */
  backLabel?: string;
  onBack: () => void;
  /** After a successful release — back to the dashboard. */
  onReleased: () => void;
  /** After a resolving decision — back to the dashboard (the item leaves My items). */
  onDecided?: () => void;
}

export function PhysicianReviewItemView({ itemId, backLabel = "Review Queue", onBack, onReleased, onDecided }: PhysicianReviewItemViewProps) {
  const [releasing, setReleasing] = useState(false);
  const [notFoundAfterAction, setNotFoundAfterAction] = useState(false);

  // Cached per item: reopening an item shows it at once, then refreshes it quietly.
  const query = useCachedQuery(physicianQueryKeys.reviewItem(itemId), () => getPhysicianReviewItemApi(itemId));
  const { cache } = query;
  const load = () => void query.refresh();
  // Live: a patient answering (or resolving it with "I no longer take this")
  // arrives as a notification / queue event — refetch so the screen is current.
  usePhysicianReviewLive(load, load);
  const state: ScreenState = notFoundAfterAction
    ? { kind: "not-found" }
    : query.data
      ? { kind: "ready", item: query.data }
      : query.loading || !query.error
        ? { kind: "loading" }
        : { kind: query.error.code === "REVIEW_ITEM_NOT_FOUND" ? "not-found" : "load-failed" };
  const setState = (next: { kind: "ready"; item: PhysicianReviewItem } | { kind: "not-found" }) => {
    if (next.kind === "ready") query.mutate(() => next.item);
    else setNotFoundAfterAction(true);
  };
  // After a release or a resolving decision the item has left My items — drop it
  // there right away (no stale row when the dashboard reappears) and forget this
  // item's cached copy, so it's reloaded fresh if opened again.
  const forgetItem = () => {
    const mine = cache.get<{ id: string }[]>(physicianQueryKeys.mine);
    if (mine) cache.set(physicianQueryKeys.mine, mine.filter((i) => i.id !== itemId));
    cache.invalidate(physicianQueryKeys.reviewItem(itemId));
  };

  const item = state.kind === "ready" ? state.item : null;
  const meds = useMemo(() => (item ? toMedications(item) : []), [item]);
  const rxById = useMemo(() => new Map(item?.records.map((r) => [r.id, r.rxnorm]) ?? []), [item]);
  // Instructions and prescriber, read from the same keys the patient adapter uses
  // (dosage_text, requester_ref) — "Not specified" only when actually missing/empty.
  const extrasById = useMemo(
    () =>
      new Map(
        item?.records.map((r) => [
          r.id,
          {
            instructions: nonEmpty(r.fields.dosage_text),
            prescriber: nonEmpty(r.fields.requester_ref).replace(/^Practitioner\//, ""),
          },
        ]) ?? [],
      ),
    [item],
  );

  const release = async () => {
    if (!item) return;
    setReleasing(true);
    const { error } = await releaseReviewItemApi(item.id);
    setReleasing(false);
    if (!error) {
      toastSuccess(MESSAGES.released);
      forgetItem();
      onReleased();
    } else if (error.code === "ITEM_NOT_RELEASABLE") {
      toastError(MESSAGES.notReleasable);
      load();
    } else if (error.code === "REVIEW_ITEM_NOT_FOUND") {
      setState({ kind: "not-found" });
    } else {
      toastError(error.message);
    }
  };

  if (state.kind !== "ready") {
    return (
      <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
        <div className="mb-3"><BackArrow onClick={onBack} label={backLabel} /></div>
        {state.kind === "loading" ? (
          <PageSpinner className="bg-transparent" />
        ) : (
          <div className="mx-auto flex max-w-xl flex-col items-center rounded-3xl border border-[#E5E7EB] bg-white px-6 py-12 text-center shadow-sm" role="status">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F3F4F6]">
              <Icon icon={state.kind === "not-found" ? "lucide:file-x" : "lucide:wifi-off"} className="h-7 w-7 text-[#9CA3AF]" />
            </div>
            <p className="mt-4 text-base font-extrabold text-[#0A2947]">{state.kind === "not-found" ? MESSAGES.notFound : MESSAGES.loadFailed}</p>
            <Button
              onClick={onBack}
              className="!mt-6 !h-10 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-6 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white"
            >
              Back to {backLabel}
            </Button>
          </div>
        )}
      </div>
    );
  }

  const it = state.item;
  const isPair = isPairItem(meds);

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      <div className="grid gap-5 lg:grid-cols-[1fr_380px]">
        <div className="grid content-start gap-6 rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <BackArrow onClick={onBack} label={backLabel} />
              <div>
                <p className="text-lg font-extrabold text-[#0A2947]">{it.patient.displayName}</p>
                <p className="text-xs text-[#6B7280]">Date of birth {formatDate(it.patient.dateOfBirth)}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ReviewStatusBadge status={it.itemStatus} />
              <AttachedViaTag via={it.attachedVia} />
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#E4002B]/50">
              <Icon icon={isPair ? "lucide:git-compare" : "lucide:circle-help"} className="h-4 w-4 !text-white" />
            </div>
            <h2 className="text-base font-extrabold text-[#0A2947]">{ISSUE_LABELS[it.triggerType]}</h2>
          </div>

          {/* Records + RxNorm */}
          <ReviewRecords
            records={meds}
            renderExtra={(med) => (
              <>
                {[
                  { label: "Instructions", value: extrasById.get(med.id)?.instructions, testId: "record-instructions" },
                  { label: "Prescriber", value: extrasById.get(med.id)?.prescriber, testId: "record-prescriber" },
                ].map(({ label, value, testId }) => (
                  <div key={label} className="mt-2 flex items-baseline justify-between gap-3" data-testid={testId}>
                    <span className="text-xs text-[#9CA3AF]">{label}</span>
                    <span className="text-right text-sm font-semibold text-[#0A2947]">{value || "Not specified"}</span>
                  </div>
                ))}
                <RxNormBlock match={rxById.get(med.id) ?? null} />
              </>
            )}
          />

          {/* Patient Conversation: questions, answers and the decision, in order */}
          <ReviewHistory item={it} isPair={isPair} />
        </div>

        {/* Decision panel (Module 5) + release */}
        <aside className="grid content-start gap-4">
          <DecisionPanel
            key={`${it.id}:${it.itemStatus}`}
            item={it}
            onResolved={() => {
              forgetItem();
              (onDecided ?? onBack)();
            }}
            onItemChanged={(updated) => (updated ? setState({ kind: "ready", item: updated }) : load())}
            onNotFound={() => setState({ kind: "not-found" })}
            // Same line as "Record decision", inside the panel.
            secondaryAction={it.itemStatus === "IN_PHYSICIAN_REVIEW" ? (
              <Button
                variant="outline"
                busy={releasing}
                onClick={() => void release()}
                className="!h-10 !w-full !rounded-lg !border-[#E5E7EB] !text-sm !font-semibold !text-[#4B5563] hover:!border-[#E4002B] hover:!text-[#E4002B]"
              >
                Release to queue
              </Button>
            ) : undefined}
          />
        </aside>
      </div>

    </div>
  );
}
